// src/components/NonCreatorDashboard.js

import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import './nonCreatorDashboard.css';

// FIREBASE
import {
  doc,
  onSnapshot,
  collection,
  getDoc,
  updateDoc,
  setDoc,
  arrayUnion
} from 'firebase/firestore';
import { db, auth } from '../../firebase';

// FUNCTIONS
import LinksNotification from '../../components/appComponents/linksNotification';
import AppTransition from '../../components/appComponents/AppTransition';
import axios from 'axios';
import { debounce } from 'lodash';
import { getYoutubeURLUnAuthCaptions } from '../myBumpUp/myCreator/getVideoCaptions';

// IMPORT UI
import NonAuthThumbnailLayout from './nonAuthThumbnailLayout';
import Sidebar from '../../myDashboard/Sidebar';
import CloseSidebar from '../../myDashboard/CloseSidebar';
import UserStudio from '../UserStudio';

// IMAGES
import processBumpupslogo from '../../assets/gifmedia/bumpups-2.gif';
import info_icon from '../../assets/images/icons/info-icon.png';
import preview_thumbnail from '../../assets/images/thumbnail/preview-thumbnail.png';
import link_icon from '../../assets/images/icons/link-icon.png';

function NonCreatorDashboard({ isSidebarOpen, toggleSidebar }) {
  const [youtubeVideoDetails, setYoutubeVideoDetails] = useState({});
  const [youtubeInputUrl, setYoutubeInputUrl] = useState('');

  const [isSearching, setIsSearching] = useState(false);
  const [istoLong, setistoLong] = useState(false);
  const [noValidVideo, setnoValidVideo] = useState(false);
  const [captionsAvailable, setcaptionsAvailable] = useState(false);

  const [isOverTimebank, setOverTimebank] = useState(false);
  const [isShortTimestamp, setIsShortTimestamp] = useState(false);
  const [showVideoNotFound, setShowVideoNotFound] = useState(false);

  const [doneVideos, setDoneVideos] = useState([]);
  const [processingStatus, setProcessingStatus] = useState({});
  const [videoProcessingError, setVideoProcessingError] = useState({});

  const [countdown, setCountdown] = useState(20);
  const [finalizingUpload, setFinalizingUpload] = useState(false);

  // NEW: creditsUsed, creditsLimit & progressIntervalRef
  const [creditsUsed, setCreditsUsed]     = useState(0);
  const [creditsLimit, setCreditsLimit]   = useState(0);
  const progressIntervalRef = useRef(null);

  // compute remaining
  const remainingCredits = Math.max(creditsLimit - creditsUsed, 0);

  // Helpers
  const extractYoutubeVideoID = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=|youtube\.com\/live\/)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && /^[a-zA-Z0-9_-]{11}$/.test(match[2]) ? match[2] : null;
  };

  const convertDurationToSeconds = (duration) => {
    if (!duration) return 0;
    const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
    if (!match) return 0;
    const hours = match[1] ? parseInt(match[1], 10) : 0;
    const minutes = match[2] ? parseInt(match[2], 10) : 0;
    const seconds = match[3] ? parseInt(match[3], 10) : 0;
    return hours * 3600 + minutes * 60 + seconds;
  };

  // 1) Fetch & subscribe to planDetails → creditsUsed & creditsLimit
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;

    const planRef = doc(
      db,
      'users',
      user.uid,
      'subscriptionData',
      'planDetails'
    );

    // initial load
    getDoc(planRef)
      .then((snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setCreditsUsed(data.creditsUsed ?? 0);
          setCreditsLimit(data.creditsLimit ?? 0);
        }
      })
      .catch(() => {});

    // realtime updates
    const unsub = onSnapshot(planRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        setCreditsUsed(data.creditsUsed ?? 0);
        setCreditsLimit(data.creditsLimit ?? 0);
      }
    });

    return () => unsub();
  }, []);

  // 2) Recompute overTimebank purely from remainingCredits
  useEffect(() => {
    if (!youtubeVideoDetails.duration) {
      setOverTimebank(false);
      return;
    }
    const durSec = convertDurationToSeconds(youtubeVideoDetails.duration);
    setOverTimebank(durSec > remainingCredits * 60);
  }, [youtubeVideoDetails, remainingCredits]);

  // Fetch timestamp preference
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const tsRef = doc(db, 'users', user.uid, 'valueSettings', 'timestamps');
    getDoc(tsRef)
      .then((snap) => {
        if (snap.exists()) {
          setIsShortTimestamp(snap.data().isShortTimestamps || false);
        }
      })
      .catch(() => {});
  }, []);

  const debouncedUpdateTimestampLength = debounce(async (isShort) => {
    const user = auth.currentUser;
    if (!user) return;
    const tsRef = doc(db, 'users', user.uid, 'valueSettings', 'timestamps');
    try {
      await updateDoc(tsRef, { isShortTimestamps: isShort });
    } catch {}
    setIsShortTimestamp(isShort);
  }, 500);

  const handleTimestampLengthChange = (e) => {
    debouncedUpdateTimestampLength(e.target.checked);
  };

  // Fetch YouTube details & captions
  const fetchYoutubeVideoDetails = async (url) => {
    const videoId = extractYoutubeVideoID(url);
    if (!videoId) return;

    setIsSearching(true);
    const apiKey = process.env.REACT_APP_YOUTUBE_API_KEY;

    try {
      const { data } = await axios.get(
        'https://www.googleapis.com/youtube/v3/videos',
        {
          params: {
            part: 'snippet,contentDetails,status',
            id: videoId,
            key: apiKey,
          },
        }
      );

      if (!data.items.length) {
        setnoValidVideo(true);
        setIsSearching(false);
        return;
      }

      const { snippet, contentDetails, status } = data.items[0];
      const durationSec = convertDurationToSeconds(contentDetails.duration);
      setistoLong(durationSec > 12600);

      const thumbnailUrl =
        snippet.thumbnails.maxres?.url ||
        snippet.thumbnails.standard?.url ||
        snippet.thumbnails.high?.url;
      if (!thumbnailUrl) {
        setIsSearching(false);
        return;
      }

      let captionsFound = true;
      if (status.privacyStatus === 'public') {
        const capRes = await axios.get(
          'https://www.googleapis.com/youtube/v3/captions',
          { params: { videoId, key: apiKey } }
        );
        captionsFound = capRes.data.items.length > 0;
      }
      setcaptionsAvailable(captionsFound);

      setYoutubeVideoDetails({
        thumbnailUrl,
        duration: contentDetails.duration,
        language: snippet.defaultLanguage || 'Language unspecified',
        visibility: status.privacyStatus,
        videoId,
        title: snippet.title,
      });

      setIsSearching(false);
    } catch {
      setIsSearching(false);
    }
  };

  // Handle URL input changes
  const handleUrlChange = (url) => {
    setYoutubeInputUrl(url);
    setnoValidVideo(false);
    setistoLong(false);
    setOverTimebank(false);
    setcaptionsAvailable(false);
    setShowVideoNotFound(false);

    const videoId = extractYoutubeVideoID(url);
    if (videoId) {
      fetchYoutubeVideoDetails(url);
    } else {
      setYoutubeVideoDetails({});
      setShowVideoNotFound(true);
    }
  };

  // Process YouTube URL
  const handleYoutubeURLProcessing = async () => {
    setIsSearching(true);
    const { videoId, duration, thumbnailUrl, title, visibility } =
      youtubeVideoDetails;
    const durationInSeconds = convertDurationToSeconds(duration);
    const isPublic = visibility === 'public';
    const contentId = `youtubeUrl-${videoId}`;

    setProcessingStatus((ps) => ({ ...ps, [videoId]: true }));

    try {
      const { message } = await getYoutubeURLUnAuthCaptions({
        videoId,
        durationInSeconds,
        thumbnailUrl,
        title,
        isReprocess: false,
        fullUrl: youtubeInputUrl,
        isPublic,
        contentId,
      });

      if (message !== 'success') {
        setProcessingStatus((ps) => ({ ...ps, [videoId]: false }));
        setVideoProcessingError((err) => ({ ...err, [videoId]: message }));
        setTimeout(() => {
          setVideoProcessingError((err) => ({ ...err, [videoId]: null }));
        }, 20000);
      }
    } catch {
      setProcessingStatus((ps) => ({ ...ps, [videoId]: false }));
      setVideoProcessingError((err) => ({
        ...err,
        [videoId]: 'An error occurred during processing.',
      }));
      setTimeout(() => {
        setVideoProcessingError((err) => ({ ...err, [videoId]: null }));
      }, 20000);
    }

    setTimeout(() => {
      setVideoProcessingError((err) => ({
        ...err,
        [youtubeVideoDetails.videoId]:
          'Processing is taking longer than usual... ⏳',
      }));
      setProcessingStatus((ps) => ({
        ...ps,
        [youtubeVideoDetails.videoId]: false,
      }));
      setIsSearching(false);
    }, 600000);
  };

  // Listen for processed videos & write to Firestore
useEffect(() => {
  const user = auth.currentUser;
  if (!user || !youtubeVideoDetails.videoId) return;

  const userDocRef = doc(db, 'users', user.uid);
  const unAuthCollectionRef = collection(userDocRef, 'youtubeUrlContent');

  const unsubscribe = onSnapshot(unAuthCollectionRef, async (qs) => {
    const processedIds = qs.docs.map(d =>
      d.id.startsWith('youtubeUrl-') ? d.id.slice(11) : d.id
    );

    if (processedIds.includes(youtubeVideoDetails.videoId)) {
      // 1) clear any “processing” UI immediately
      setProcessingStatus(ps => ({
        ...ps,
        [youtubeVideoDetails.videoId]: false
      }));
      setIsSearching(false);
      setFinalizingUpload(false);
      clearInterval(progressIntervalRef.current);

      // 2) mark this video as done so the “Upload Another” button shows
      setDoneVideos(prev => [...prev, youtubeVideoDetails.videoId]);

      // 3) now do your Firestore write
      const truncatedTitle =
        youtubeVideoDetails.title.length > 52
          ? youtubeVideoDetails.title.slice(0, 52) + '...'
          : youtubeVideoDetails.title;

      const newVideoData = {
        durationInSeconds: convertDurationToSeconds(youtubeVideoDetails.duration),
        thumbnailUrl: youtubeVideoDetails.thumbnailUrl,
        title: truncatedTitle,
        videoId: youtubeVideoDetails.videoId,
      };

      try {
        const snap = await getDoc(unAuthCollectionRef);
        if (!snap.exists()) {
          await setDoc(unAuthCollectionRef, { videos: [newVideoData] });
        } else {
          await updateDoc(unAuthCollectionRef, {
            videos: arrayUnion(newVideoData),
          });
        }
        localStorage.removeItem(`youtubeUrlCache_${auth.currentUser.uid}`);
      } catch (err) {
        // console.error('Error saving processed video:', err);
      }
    }
  });

  return () => unsubscribe();
}, [
  youtubeVideoDetails.duration,
  youtubeVideoDetails.thumbnailUrl,
  youtubeVideoDetails.title,
  youtubeVideoDetails.videoId,
]);



  // Countdown + finalizing
  useEffect(() => {
    const vid = youtubeVideoDetails.videoId;
    if (!vid || !processingStatus[vid]) return;

    setCountdown(20);
    setFinalizingUpload(false);

    const interval = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(interval);
          setFinalizingUpload(true);
          return c;
        }
        return c - 1;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [processingStatus, youtubeVideoDetails.videoId]);

  // Reset countdown on new video
  useEffect(() => {
    setCountdown(20);
    setFinalizingUpload(false);
  }, [youtubeVideoDetails.videoId]);

  const handleUploadAnotherVideo = () => {
    setYoutubeInputUrl('');
    setYoutubeVideoDetails({});
    setDoneVideos([]);
    setProcessingStatus({});
    setVideoProcessingError({});
    setIsSearching(false);
    setnoValidVideo(false);
    setistoLong(false);
    setOverTimebank(false);
    setcaptionsAvailable(false);
  };

  return (
    <div className="uploaddashboard">
      {isSidebarOpen ? <Sidebar /> : <CloseSidebar />}
      <AppTransition>
        <UserStudio />

        <div className="dashboard-container">
          <LinksNotification />

          <section className="urlinput-section">
            <div className="urlinput-container">
              <div className="input-icon-wrapper">
                <img
                  src={link_icon}
                  alt="Link Icon"
                  className="url-input-icon"
                />
                <input
                  type="text"
                  className="url-input-box"
                  placeholder="Drop a YouTube link..."
                  value={youtubeInputUrl}
                  onChange={(e) => handleUrlChange(e.target.value)}
                />
              </div>

              <div>
                {/* Process Video */}
                {!processingStatus[youtubeVideoDetails.videoId] &&
                  !doneVideos.includes(youtubeVideoDetails.videoId) &&
                  !isOverTimebank &&
                  !istoLong &&
                  captionsAvailable &&
                  youtubeInputUrl &&
                  extractYoutubeVideoID(youtubeInputUrl) && (
                    <button
                      className={`url-input-button ${
                        isSearching ? 'searching' : ''
                      }`}
                      onClick={handleYoutubeURLProcessing}
                      disabled={
                        isSearching ||
                        noValidVideo ||
                        !extractYoutubeVideoID(youtubeInputUrl)
                      }
                    >
                      {noValidVideo
                        ? 'Not Valid URL'
                        : isSearching
                        ? 'Searching...'
                        : 'Upload Video'}
                    </button>
                  )}

                {/* No Captions */}
                {!processingStatus[youtubeVideoDetails.videoId] &&
                  !doneVideos.includes(youtubeVideoDetails.videoId) &&
                  !isOverTimebank &&
                  !istoLong &&
                  !captionsAvailable &&
                  youtubeVideoDetails.videoId && (
                    <button className="url-input-button processing" disabled>
                      No Captions
                    </button>
                  )}

                {/* Countdown */}
                {processingStatus[youtubeVideoDetails.videoId] && (
                  <button className="url-input-button processing" disabled>
                    {!finalizingUpload
                      ? `${countdown} seconds left...`
                      : 'Finalizing Upload...'}
                  </button>
                )}

                {/* Get More Time */}
                {isOverTimebank &&
                  !doneVideos.includes(youtubeVideoDetails.videoId) &&
                  !processingStatus[youtubeVideoDetails.videoId] && (
                    <Link to="/settings/plan" className="url-input-button timebank-over">
                      Get More Time
                    </Link>
                  )}

                {/* Max Duration */}
                {istoLong &&
                  !doneVideos.includes(youtubeVideoDetails.videoId) &&
                  !isOverTimebank &&
                  !processingStatus[youtubeVideoDetails.videoId] && (
                    <button
                      className="url-input-button max-duration-warning"
                      disabled
                    >
                      Max Duration 3h 30m
                    </button>
                  )}

                {/* Upload Another */}
                {doneVideos.includes(youtubeVideoDetails.videoId) && (
                  <button
                    className="url-input-button videoDataReady"
                    onClick={handleUploadAnotherVideo}
                  >
                    Upload Another Video
                  </button>
                )}
              </div>
            </div>
          </section>

          <section className="youtube-video-details">
            {youtubeInputUrl && youtubeVideoDetails.thumbnailUrl && (
              <div className="video-card">
                <div className="video-overlay"></div>
                <div
                  className="video-thumbnail-container"
                  style={{ position: 'relative' }}
                >
                  <img
                    src={
                      youtubeVideoDetails.thumbnailUrl || preview_thumbnail
                    }
                    alt="Video Thumbnail"
                    className="video-thumbnail"
                  />

                  {processingStatus[youtubeVideoDetails.videoId] && (
                    <>
                      <div className="video-thumbnail-overlay"></div>
                      <img
                        src={processBumpupslogo}
                        alt="Processing"
                        className="processing-gif"
                        style={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          zIndex: 2,
                        }}
                      />
                    </>
                  )}

                  {videoProcessingError[youtubeVideoDetails.videoId] &&
                    !doneVideos.includes(youtubeVideoDetails.videoId) && (
                      <div className="error-video-thumbnail-overlay">
                        <div className="unauth-error-message-box">
                          <div className="unauth-error-message">
                            {videoProcessingError[
                              youtubeVideoDetails.videoId
                            ] === 'captions_not_available'
                              ? 'Captions not available for this video.'
                              : 'An error occurred during processing.'}
                          </div>
                          <div className="unauth-retry-message">
                            Learn more{' '}
                            <a
                              href="https://intercom.help/bumpups/en/articles/9126173-why-won-t-my-videos-process"
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              here
                            </a>
                            .
                          </div>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            )}
          </section>

          {youtubeInputUrl && extractYoutubeVideoID(youtubeInputUrl) && (
            <div className="toggle-switch-container">
              <span className="toggle-switch-text">
                <b>Make timestamp length short?</b>
              </span>
              <a
                href="https://intercom.help/bumpups/en/articles/9108586-what-timestamp-length-is-right-for-your-content"
                target="_blank"
                rel="noopener noreferrer"
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <img
                  src={info_icon}
                  alt="Info"
                  className="info-icon-toggle"
                />
              </a>
              <label className="toggle-switch-label">
                <input
                  type="checkbox"
                  checked={isShortTimestamp}
                  onChange={handleTimestampLengthChange}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          )}

          {showVideoNotFound && youtubeInputUrl && (
            <div className="unauth-video-not-found">
              <div>
                <div className="unauth-main-text">Video Not Found 🔎</div>
                <div className="unauth-small-text">
                  check your video id:
                  <span className="unauth-example-url">
                    https://www.youtube.com/watch?v=
                    <span className="highlight-video-id">Vevu1P5XGNk</span>
                  </span>
                  <br />
                  supports videos from 5 to 240 minutes ⏲️
                </div>
              </div>
            </div>
          )}

          {!youtubeInputUrl && (
            <section className="video-type-section">
              <div className="video-type-info">
                <p>
                  This feature is optimized for videos with <b>SPOKEN</b>{' '}
                  content and currently supports YouTube links.
                </p>
              </div>
              <div className="video-types">
                <div className="supported-types">
                  <p>Timestamps</p>
                  <p>Titles</p>
                  <p>Description</p>
                  <p>Tags</p>
                  <p>Key Takeaways</p>
                </div>
                <div className="unsupported-types">
                  <p>
                    <b>No Chat Feature</b>
                  </p>
                </div>
              </div>
              <div className="example-text-seotool">
                Learn more and see an example{' '}
                <a
                  href="https://intercom.help/bumpups/en/articles/10787198-how-to-optimize-your-video-with-bump-ai-s-seo-tool"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="settings-link"
                >
                  here
                </a>
                .
              </div>
            </section>
          )}

          <NonAuthThumbnailLayout />
        </div>
      </AppTransition>
    </div>
  );
}

export default NonCreatorDashboard;